import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, ListChecks, Settings as SettingsIcon, Zap, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/", label: "Overzicht", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/sessies", label: "Laadsessies", icon: ListChecks, testid: "nav-sessions" },
  { to: "/instellingen", label: "Instellingen", icon: SettingsIcon, testid: "nav-settings" },
];

function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

export default function Layout({ children }) {
  const [dark, setDark] = useDarkMode();
  const loc = useLocation();

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card/50 fixed h-screen">
        <div className="px-6 py-7 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-head font-extrabold text-lg leading-none tracking-tight">Laadnota</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Plugchoice</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-border">
          <button
            data-testid="theme-toggle"
            onClick={() => setDark(!dark)}
            className="flex items-center gap-3 px-3 py-2.5 w-full rounded-md text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            {dark ? "Lichte modus" : "Donkere modus"}
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 bg-card border-b border-border flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2 font-head font-extrabold tracking-tight">
          <div className="h-7 w-7 rounded bg-primary flex items-center justify-center">
            <Zap className="h-4 w-4 text-primary-foreground" strokeWidth={2.5} />
          </div>
          Laadnota
        </div>
        <div className="flex gap-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) =>
              cn("p-2 rounded-md", isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
              <n.icon className="h-5 w-5" />
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 md:ml-64 pt-14 md:pt-0" key={loc.pathname}>
        <div className="max-w-6xl px-5 md:px-10 py-8 md:py-10">{children}</div>
      </main>
    </div>
  );
}
